import { useState } from "react";
import {
    Box, Stack, Tab, TabList, TabPanel, Tabs, Typography, tabClasses, Chip, IconButton, Tooltip,
} from "@mui/joy";
import { motion } from "framer-motion";

import BarChartRoundedIcon from '@mui/icons-material/BarChartRounded';
import AccountBalanceWalletRoundedIcon from '@mui/icons-material/AccountBalanceWalletRounded';
import ManageAccountsRoundedIcon from '@mui/icons-material/ManageAccountsRounded';
import SupportAgentRoundedIcon from '@mui/icons-material/SupportAgentRounded';
import LightbulbRoundedIcon from '@mui/icons-material/LightbulbRounded';
import EmojiEventsRoundedIcon from '@mui/icons-material/EmojiEventsRounded';
import LockResetRoundedIcon from '@mui/icons-material/LockResetRounded';
import LogoutRoundedIcon from '@mui/icons-material/LogoutRounded';
import BusinessCenterRoundedIcon from '@mui/icons-material/BusinessCenterRounded';
import CampaignRoundedIcon from '@mui/icons-material/CampaignRounded';
import PaidRoundedIcon from '@mui/icons-material/PaidRounded';
import PartnerTerritory from '../../components/Partner/PartnerTerritory';
import PartnerAdRevenue from '../../components/Partner/PartnerAdRevenue';
import PartnerNotificationsBell from '../../components/Partner/PartnerNotificationsBell';

import { usePartner } from "../../PartnerContext";
import PartnerStats from "../../components/Partner/PartnerStats";
import PartnerBalance from "../../components/Partner/PartnerBalance";
import PartnerProfileForm from "../../components/Partner/PartnerProfileForm";
import PartnerSupport from "../../components/Partner/PartnerSupport";
import PartnerTips from "../../components/Partner/PartnerTips";
import PartnerBonus from "../../components/Partner/PartnerBonus";
import PartnerReferralCard from "../../components/Referral/PartnerReferralCard";
import PartnerSecurity from "../../components/Partner/PartnerSecurity";


export default function PartnerCabinetPage() {
    const { partner, logout } = usePartner();
    const [tab, setTab] = useState(0);

    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
        >
            <Box sx={{ px: { xs: 2, md: 4 }, pt: 2, pb: 4, maxWidth: 1200, mx: 'auto' }}>
                {/* Шапка */}
                <Stack
                    direction="row"
                    spacing={1.5}
                    sx={{ alignItems: "center", mb: 2 }}
                    justifyContent="space-between"
                >
                    <Stack direction="row" spacing={1.5} alignItems="center">
                        <Box sx={{
                            width: 44, height: 44, borderRadius: 'md',
                            bgcolor: 'primary.softBg', color: 'primary.700',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}>
                            <BusinessCenterRoundedIcon />
                        </Box>
                        <Box>
                            <Typography level="h3" component="h1">
                                Кабинет партнёра
                            </Typography>
                            <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.3 }}>
                                <Typography level="body-sm" color="neutral">
                                    {partner?.full_name || partner?.email || ''}
                                </Typography>
                                {partner?.scope_type && (
                                    <Chip size="sm" color="primary" variant="soft">
                                        {partner.scope_type === 'region'
                                            ? `Регион: ${partner.scope_region}`
                                            : `Город: ${partner.scope_city}`}
                                    </Chip>
                                )}
                                {!partner?.is_email_verified && (
                                    <Chip size="sm" color="warning" variant="soft">
                                        Email не подтверждён
                                    </Chip>
                                )}
                            </Stack>
                        </Box>
                    </Stack>

                    <Stack direction="row" spacing={1} alignItems="center">
                        {/* Кнопка «Реклама» убрана: она вела в рекламный кабинет
                            рекламодателя, который на партнёрской ветке падал
                            (нет контекста рекламодателя), и дублировала вкладки
                            «Реклама на территории» и «Доход с рекламы» */}
                        <Tooltip title="Выйти" arrow>
                            <IconButton color="danger" variant="soft" onClick={logout}>
                                <LogoutRoundedIcon />
                            </IconButton>
                        </Tooltip>
                    </Stack>
                </Stack>

                {/* Табы */}
                <Tabs
                    value={tab}
                    onChange={(_, v) => setTab(v)}
                    sx={{ bgcolor: 'transparent' }}
                >
                    <TabList
                        sx={{
                            overflowX: 'auto',
                            scrollbarWidth: 'none',
                            '&::-webkit-scrollbar': { display: 'none' },
                            flexWrap: 'nowrap',
                            [`& .${tabClasses.root}`]: {
                                whiteSpace: 'nowrap',
                                minWidth: 'fit-content',
                                flexShrink: 0,
                            },
                            [`& .${tabClasses.root}.${tabClasses.selected}`]: {
                                bgcolor: 'primary.softBg',
                                color: 'primary.700',
                            },
                            mb: 2,
                            borderRadius: 'md',
                        }}
                    >
                        <Tab><BarChartRoundedIcon sx={{ mr: 0.5 }} fontSize="small" /> Статистика</Tab>
                        <Tab><AccountBalanceWalletRoundedIcon sx={{ mr: 0.5 }} fontSize="small" /> Баланс</Tab>
                        <Tab><ManageAccountsRoundedIcon sx={{ mr: 0.5 }} fontSize="small" /> Данные</Tab>
                        <Tab><SupportAgentRoundedIcon sx={{ mr: 0.5 }} fontSize="small" /> Поддержка</Tab>
                        <Tab><LightbulbRoundedIcon sx={{ mr: 0.5 }} fontSize="small" /> Советы</Tab>
                        <Tab><EmojiEventsRoundedIcon sx={{ mr: 0.5 }} fontSize="small" /> Бонусы</Tab>
                        <Tab><LockResetRoundedIcon sx={{ mr: 0.5 }} fontSize="small" /> Безопасность</Tab>
                        <Tab><CampaignRoundedIcon sx={{ mr: 0.5 }} fontSize="small" /> Реклама на территории</Tab>
                        <Tab><PaidRoundedIcon sx={{ mr: 0.5 }} fontSize="small" /> Доход с рекламы</Tab>
                    </TabList>

                    <TabPanel value={0} sx={{ p: 0 }}><PartnerStats /></TabPanel>
                    <TabPanel value={1} sx={{ p: 0 }}><PartnerBalance /></TabPanel>
                    <TabPanel value={2} sx={{ p: 0 }}><PartnerProfileForm /></TabPanel>
                    <TabPanel value={3} sx={{ p: 0 }}><PartnerSupport /></TabPanel>
                    <TabPanel value={4} sx={{ p: 0 }}><PartnerTips /></TabPanel>
                    <TabPanel value={5} sx={{ p: 0 }}><PartnerBonus /></TabPanel>
                    <TabPanel value={6} sx={{ p: 0 }}><PartnerSecurity /></TabPanel>
                    <TabPanel value={7} sx={{ p: 0 }}><PartnerTerritory /></TabPanel>
                    <TabPanel value={8} sx={{ p: 0 }}><PartnerAdRevenue /></TabPanel>
                </Tabs>

                <PartnerReferralCard />
            </Box>
        </motion.div>
    );
}